import { z } from 'zod';
import { localizedString } from './text';
import { semanticQuerySchema } from './semantic';

export const widgetTypeSchema = z.enum(['kpi', 'chart', 'grid', 'text']);
export type WidgetType = z.infer<typeof widgetTypeSchema>;

export const widgetChartSchema = z.object({
  /** Chart kind understood by the design-system `Chart` (`bar`, `line`, `area`, …). */
  type: z.string().min(1),
  /** Column used for the x axis (default: first dimension column). */
  xKey: z.string().optional(),
  /** Columns plotted as series (default: every metric column). */
  seriesKeys: z.array(z.string()).optional(),
});
export type WidgetChart = z.infer<typeof widgetChartSchema>;

export const dashboardWidgetSchema = z.object({
  id: z.string().min(1),
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
  type: widgetTypeSchema,
  title: localizedString,
  query: semanticQuerySchema.optional(),
  chart: widgetChartSchema.optional(),
  /** Markdown-lite content for `text` widgets (paragraphs, **bold**). */
  text: z.string().optional(),
});
export type DashboardWidget = z.infer<typeof dashboardWidgetSchema>;

export const dashboardDefinitionSchema = z
  .object({
    key: z.string().min(1),
    title: localizedString.optional(),
    widgets: z.array(dashboardWidgetSchema),
  })
  .superRefine((def, ctx) => {
    const ids = new Set<string>();
    def.widgets.forEach((w, index) => {
      if (ids.has(w.id)) ctx.addIssue({ code: 'custom', path: ['widgets', index, 'id'], message: `Duplicate widget id "${w.id}"` });
      ids.add(w.id);
      if (w.type !== 'text' && !w.query) ctx.addIssue({ code: 'custom', path: ['widgets', index, 'query'], message: `${w.type} widget "${w.id}" needs a query` });
    });
  });
export type DashboardDefinition = z.infer<typeof dashboardDefinitionSchema>;
